// Edge PLC-applications page. Split from platformEdgeRuntimes.components.js;
// re-exported by that barrel.
//
// A page rather than a dialog, for the same reason "I/O & connections" is one:
// the two are peer destinations from the runtime detail view, and the list here
// is unbounded — one row per deployed application, each carrying sync state,
// run state, command status, failure detail, delivery deadline and build
// identity. Start/Stop acts on a live controller, and a dialog compresses
// exactly the evidence an operator should read before stopping one.
import React, { useCallback, useEffect, useRef, useState } from "react";

import { GatewayStatusPill } from "../../../components/GatewayStatus";
import { listEdgePLCPrograms, setEdgePLCProgramEnabled } from "../../../api";
import { useNotify } from "../../../ToastContext";
import chevronIcon from "../../../assets/chevron-right.svg";
import {
  PLC_SYNC_LABELS,
  edgeCommandDeadlineView,
  plcBuildIdentityView,
  plcCommandLabel,
  plcSyncPillClass,
} from "./platformEdgeRuntimes.model";

export function PLCProgramsPage({ gateway, canWrite, onClose, onChanged }) {
  const notify = useNotify();
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState("");
  const mountedRef = useRef(true);
  const loadGenerationRef = useRef(0);

  useEffect(() => {
    // React development builds may mount, clean up, and mount an effect again
    // to expose unsafe lifecycles. Re-arm the guard for that second setup.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadGenerationRef.current += 1;
    };
  }, []);

  const load = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    try {
      const next = await listEdgePLCPrograms(gateway.id);
      if (!mountedRef.current || generation !== loadGenerationRef.current) return;
      setInventory(next);
      setLoadError("");
    } catch {
      if (!mountedRef.current || generation !== loadGenerationRef.current) return;
      setLoadError("Could not read PLC applications from this device. Try again.");
    } finally {
      if (mountedRef.current && generation === loadGenerationRef.current) setLoading(false);
    }
  }, [gateway.id]);

  useEffect(() => {
    let cancelled = false;
    let refreshing = false;
    const refresh = async () => {
      if (cancelled || refreshing) return;
      refreshing = true;
      await load();
      refreshing = false;
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [load]);

  const setEnabled = async (program, enabled) => {
    setBusy(program.source_program_id);
    try {
      await setEdgePLCProgramEnabled(gateway.id, program.source_program_id, enabled);
      if (!mountedRef.current) return;
      notify({
        type: "success",
        title: enabled ? "Start queued" : "Stop queued",
        message: `${program.name} will ${enabled ? "start" : "stop"} when ${gateway.name} acknowledges the command.`,
      });
      await load();
      await onChanged();
    } catch {
      if (!mountedRef.current) return;
      notify({ type: "error", title: enabled ? "Start failed" : "Stop failed", message: "The device did not accept this change. Check its connection and try again." });
    } finally {
      if (mountedRef.current) setBusy("");
    }
  };

  const programs = Array.isArray(inventory?.programs) ? inventory.programs : [];
  return (
    <div className="gwConnectionsPage">
      <div className="gwConnectionsPageInner">
        <div className="gwPageHeader gwConnectionsPageHeader">
          <div>
            <button type="button" className="gwGhostBtn plcRuntimeDetailBack" onClick={onClose}>
              <img src={chevronIcon} alt="" className="gwIcon" />
              {gateway.name}
            </button>
            <div className="gwPageTitle" id="gw-edge-programs-title">Applications on {gateway.name}</div>
            <div className="gwPageSubtitle" id="gw-edge-programs-description">
              PLC applications and the latest run state reported by this runtime.
            </div>
          </div>
          {/* Refresh belongs in the header on a page, matching the I/O page's
              icon button, so it stays reachable without scrolling a long list. */}
          <div className="gwHeaderActions">
            <button
              type="button"
              className="gwGhostBtn"
              disabled={loading}
              onClick={async () => { setLoading(true); await Promise.all([load(), onChanged()]); }}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
        <div className="gwConnectionsPageBody gwEdgeProgramsBody">
          {inventory ? (
            <div className={`gwNotice ${inventory.snapshot_stale ? "isSoft" : ""}`}>
              <strong>{inventory.connection === "online" && !inventory.snapshot_stale ? "Device online" : "Device snapshot is stale"}</strong>
              <span>
                {inventory.snapshot_stale
                  ? " Values and execution state are the last confirmed snapshot; queued changes apply after reconnection."
                  : " Version, run state, and scan status below were confirmed by this device."}
              </span>
            </div>
          ) : null}
          {inventory?.snapshot_error ? <div className="gwNotice isSoft">The latest device status is temporarily unavailable.</div> : null}
          {loadError ? (
            <div className="gwAlert" role="alert">
              <div className="gwAlertTitle">Controller inventory unavailable</div>
              <div className="gwAlertBody">{loadError}</div>
            </div>
          ) : null}
          {loading && !inventory ? <div className="gwHint">Reading controller inventory…</div> : null}
          {!loading && !loadError && programs.length === 0 ? (
            <div className="gwEmptyState gwEmptyCompact">
              <div className="gwEmptyTitle">No PLC applications deployed</div>
              <div className="gwEmptyBody">Open a PLC application in the editor, publish it, then deploy it to this runtime.</div>
            </div>
          ) : null}
          {programs.length > 0 ? (
            <div className="gwEdgeProgramList">
              {programs.map((program, index) => {
                const runtimeState = program.actual?.runtime?.state || (program.actual?.present ? "unknown" : "not present");
                const runtimeStateLabel = runtimeState === "running"
                  ? "Running"
                  : runtimeState === "stopped"
                    ? "Stopped"
                    : runtimeState === "binding_error"
                      ? "Binding error"
                      : runtimeState === "faulted"
                        ? "Faulted"
                        : runtimeState === "unknown"
                          ? "State unknown"
                          : runtimeState;
                const desiredEnabled = Boolean(program.desired?.enabled);
                const actionPending = busy === program.source_program_id;
                const retryDesiredState = program.sync_state === "run_state_mismatch" || program.sync_state === "faulted";
                const requestedEnabled = retryDesiredState ? desiredEnabled : !desiredEnabled;
                const actionLabel = retryDesiredState
                  ? `Retry ${desiredEnabled ? "start" : "stop"}`
                  : desiredEnabled ? "Stop" : "Start";
                const buildIdentity = plcBuildIdentityView(program);
                const commandDeadline = edgeCommandDeadlineView(program.last_command);
                return (
                  <div className="gwEdgeProgramRow" key={program.source_program_id || `${program.name}-${index}`}>
                    <div className="gwEdgeProgramMain">
                      <div className="gwEdgeRuntimeTitleRow">
                        <span className="gwListTitle">{program.name || "Unnamed program"}</span>
                        <GatewayStatusPill tone={plcSyncPillClass(program.sync_state)}>
                          {PLC_SYNC_LABELS[program.sync_state] || "Unknown"}
                        </GatewayStatusPill>
                      </div>
                      <div className="gwListMeta">
                        {runtimeStateLabel}
                      </div>
                      {program.desired?.command_status === "queued" || program.desired?.command_status === "sent" ? (
                        <div className="gwListMeta">Command {program.desired.command_status}; waiting for device confirmation</div>
                      ) : null}
                      {program.last_command?.status === "failed" ? (
                        <div className="gwListMeta">Last {plcCommandLabel(program.last_command.kind)} failed: {program.last_command.error || "no error detail reported"}</div>
                      ) : null}
                      {commandDeadline ? (
                        <div
                          className={`gwListMeta ${commandDeadline.state === "warning" ? "isWarning" : ""}`}
                          role={commandDeadline.state === "warning" ? "alert" : undefined}
                        >
                          {commandDeadline.label}
                        </div>
                      ) : null}
                      {program.managed && !program.desired?.present ? (
                        <div className="gwListMeta">Deploy a published version from the PLC editor before changing run state.</div>
                      ) : null}
                      {buildIdentity ? (
                        <div className={`gwEdgeBuildIdentity is-${buildIdentity.state}`}>
                          <strong>{buildIdentity.label}</strong>
                          <span>{buildIdentity.detail}</span>
                        </div>
                      ) : null}
                    </div>
                    {program.managed && program.desired?.present ? (
                      <button
                        type="button"
                        className="gwRowAction"
                        disabled={!canWrite || actionPending || program.sync_state === "pending" || program.sync_state === "unknown"}
                        onClick={() => setEnabled(program, requestedEnabled)}
                        title={!canWrite ? "Write permission is required to start or stop a PLC program." : undefined}
                      >
                        {actionPending ? "Queuing…" : actionLabel}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
          {!canWrite && programs.some((program) => program.managed && program.desired?.present) ? (
            <div className="gwHint">Write permission is required to start or stop PLC programs.</div>
          ) : null}
          <div className="gwHint">Each PLC application keeps its own schedule, run state, and force set.</div>
        </div>
      </div>
    </div>
  );
}
